import React, { useState, useEffect, useCallback, useContext } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_OPENING_STOCK_REPORT, BASE_URL } from '../../utlilities/config';
import axios from 'axios';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Helper function to group data by InvoiceNo
const groupByInvoice = (data) => {
  const grouped = {};
  data.forEach(item => {
    const key = `${item.Sequence}_${item.InvoiceNo}`;
    if (!grouped[key]) {
      grouped[key] = {
        InvoiceNo: item.InvoiceNo,
        Sequence: item.Sequence,
        EntryDate: item.EntryDate,
        items: []
      };
    }
    grouped[key].items.push(item);
  });
  return Object.values(grouped);
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],
};

export const OpeningStockBillWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [stockData, setStockData] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const token = localStorage.getItem('accessToken');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);


    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getOpeningStockReport();
    };

    const handleExport = () => {
        // Export uses all groupedData, not paginated data
        if (groupedData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        toast.info('Exporting to CSV...', { autoClose: 1000 });
        // Simulate export - when implementing actual export, use groupedData (all data)
        setTimeout(() => {
            toast.success(`CSV exported successfully with ${groupedData.length} invoices`, { autoClose: 2000 });
        }, 1500);
    };

    const getOpeningStockReport = useCallback(async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            const response = await axios.get(`${BASE_URL}${GET_OPENING_STOCK_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'departmentID': localStorage.getItem('department_id') || '',
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order before grouping
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setStockData(sortedData);
                setGroupedData(groupByInvoice(sortedData));
                // Reset to page 1 when new data is loaded
                setCurrentPage(1);
            } else {
                toast.error('Failed to fetch opening stock report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch opening stock report:', error);
            toast.error('Failed to fetch opening stock report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token]);

    // Call API on page load with today's date
    useEffect(() => {
        getOpeningStockReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pagination calculations
    const totalPages = Math.ceil(groupedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedGroupedData = groupedData.slice(startIndex, endIndex);


    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5">
                    <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:items-center lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
                        {!embedded && (
                            <div className="min-w-0 shrink-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Opening Stock Reports
                                </p>
                                <h1 className="text-lg font-bold text-slate-800">
                                    Opening Stock Bill Wise
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            {reportConfig.filters.map((filter) => {
                                if (filter.type === 'date') {
                                    return (
                                        <label key={filter.id} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                            <span className="whitespace-nowrap">{filter.label}:</span>
                                            <input
                                                type="date"
                                                value={filters[filter.id] ?? ''}
                                                onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                            />
                                        </label>
                                    );
                                }
                                return null;
                            })}
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => setFilters(buildInitialFilters(reportConfig.filters))}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden px-4 py-4 flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, groupedData.length)}</span> of <span className="font-semibold text-slate-700">{groupedData.length}</span> invoices with <span className="font-semibold text-slate-700">{stockData.length}</span> items.
                                    </p>
                                    {/* Items per page selector */}

                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex-1 overflow-auto min-h-0 space-y-4">
                                {paginatedGroupedData.length > 0 ? (
                                    paginatedGroupedData.map((invoice, index) => (
                                        <div key={`${invoice.Sequence}_${invoice.InvoiceNo}`} className="rounded-xl border border-slate-200 overflow-hidden">
                                            {/* Invoice Header */}
                                            <div className="bg-indigo-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-semibold text-indigo-700">
                                                        Invoice No: {invoice.InvoiceNo}
                                                    </span>
                                                    <span className="text-sm text-slate-600">
                                                        Date: {new Date(invoice.EntryDate).toLocaleDateString('en-GB', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm text-slate-600">
                                                        Items: <span className="font-semibold">{invoice.items.length}</span>
                                                    </span>
                                                    <span className="text-sm text-slate-600">
                                                        Total Qty: <span className="font-semibold">{invoice.items.reduce((sum, item) => sum + parseFloat(item.Qty || 0), 0).toFixed(2)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Invoice Items Table */}
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                                    <thead className="bg-slate-50">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">SI No</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Item Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Batch No</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Qty</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Remarks</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-slate-100">
                                                        {invoice.items.map((item, itemIndex) => (
                                                            <tr key={itemIndex} className="hover:bg-indigo-50/30">
                                                                <td className="px-4 py-2 text-slate-700">{itemIndex + 1}</td>
                                                                <td className="px-4 py-2 text-slate-700 font-medium">{item.ItemName || '-'}</td>
                                                                <td className="px-4 py-2 text-slate-700">{item.BatchNo || '-'}</td>
                                                                <td className="px-4 py-2 text-slate-700 text-right">{parseFloat(item.Qty || 0).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-slate-500">{item.Remarks || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="rounded-xl border border-slate-200 px-4 py-8 text-center text-slate-500">
                                        No data available for the selected period
                                    </div>
                                )}
                            </div>

                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(OpeningStockBillWise);